import React, { useState, useEffect } from 'react';
import MainContentHeader from '../Layout/MainContentHeader';
import ConflictModal from './ConflictModal';
import BulkConflictModal from './BulkConflictModal';
import HoursWorkedModal from './HoursWorkedModal';
import ResultsTableSection from './ResultsTableSection';
import './ResultsContent.css';
import saveIcon from './../../assets/save-icon.png';
import useResultsData from '../../hooks/useResultsData';
import useProcessingStatus from '../../hooks/useProcessingStatus';
import ProcessingModal from './ProcessingModal';
import Sidebar from './Sidebar';
import whiteTrashIcon from '../../assets/white-trash-icon.png';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import ValidationErrorModal from './ValidationErrorModal';
import { archiveProject, saveData, submitFinalData } from '../../services/api';
import addManuallyIcon from './../../assets/add-manually-icon.png';
import uploadFileIcon from './../../assets/upload-file-icon.png';
import { 
  filterData, 
  calculateGuidantEstimatedHours, 
  defaultFieldData, 
  getBaseUrl, 
  formatDate,
  normalizeEmployeeFields,
  generateEmployeeKey
} from './ResultsContentUtils';

function ResultsContent({ currentProject, setCurrentView, customerNumber, currentView, setIsLoading, iFinanceId, priorYearIFinanceId, censusYearData }) {
  const {
    data,
    oldData,
    reportStartDates,
    reportEndDates,
    initialEditableRowData,
    setData,
    setOldData,
    sources,
    fetchResults,
    currentYearAlerts,
    previousYearAlerts
  } = useResultsData(currentProject, false, currentView, setIsLoading, iFinanceId, censusYearData, customerNumber);

  const {
    processingStatus,
    showProcessingModal,
    setShowProcessingModal,
    checkProcessingStatusAndShowModal
  } = useProcessingStatus(currentProject, fetchResults);

  const [activeTab, setActiveTab] = useState('viewAll');
  const [currentPage, setCurrentPage] = useState(1);
  const [oldCurrentPage, setOldCurrentPage] = useState(1);
  const [showCurrentYearTable, setShowCurrentYearTable] = useState(true);
  const [showPreviousYearTable, setShowPreviousYearTable] = useState(true);
  const [isAddingNewRow, setIsAddingNewRow] = useState(false);
  const [isAddingOldNewRow, setIsAddingOldNewRow] = useState(false);
  const [editableRowIndex, setEditableRowIndex] = useState(null);
  const [editableOldRowIndex, setEditableOldRowIndex] = useState(null);
  const [editableRowData, setEditableRowData] = useState(initialEditableRowData);
  const [editableOldRowData, setEditableOldRowData] = useState(initialEditableRowData);
  const [currentYearSearchTerm, setCurrentYearSearchTerm] = useState('');
  const [previousYearSearchTerm, setPreviousYearSearchTerm] = useState('');
  const [currentYearAlertFilters, setCurrentYearAlertFilters] = useState([]);
  const [previousYearAlertFilters, setPreviousYearAlertFilters] = useState([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [selectedEmployeeFullName, setSelectedEmployeeFullName] = useState('');
  const [conflictValues, setConflictValues] = useState([]);
  const [isOldDataSaving, setIsOldDataSaving] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);
  const [selectedEmployeeKey, setSelectedEmployeeKey] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [idDeletingOldData, setIsDeletingOldData] = useState(null);
  const [employeeKeyToDelete, setEmployeeKeyToDelete] = useState('');
  const [deleteMessage, setDeleteMessage] = useState('');
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [saveRequest, setSaveRequest] = useState(false);
  const [hoursFinalValue, setHoursFinalValue] = useState('');
  const [hoursWorkedNoteFinalValue, setHoursWorkedNoteFinalValue] = useState('');
  const [hoursIsOwner, setHoursIsOwner] = useState(false);
  const [hoursGrossCompensation, setHoursGrossCompensation] = useState('');
  const [showBulkConflictModal, setShowBulkConflictModal] = useState(false);
  const [bulkConflictSources, setBulkConflictSources] = useState([]);
  const [bulkConflictField, setBulkConflictField] = useState('');
  const [isOldDataBulkResolve, setIsOldDataBulkResolve] = useState(false);
  const [isRehireConflict, setIsRehireConflict] = useState(false);
  const [originalRowData, setOriginalRowData] = useState({});
  const [elevateRequiredFields, setElevateRequiredFields] = useState(false);
  
  // Auto-remove alert filters when no alerts exist
  useEffect(() => {
    setCurrentYearAlertFilters(prevFilters => {
      let updatedFilters = [...prevFilters];
      
      if (currentYearAlerts.errors === 0) {
        updatedFilters = updatedFilters.filter(f => f !== 'Error');
      }
      if (currentYearAlerts.warnings === 0) {
        updatedFilters = updatedFilters.filter(f => f !== 'Warning');
      }
      if (currentYearAlerts.invalids === 0) {
        updatedFilters = updatedFilters.filter(f => f !== 'Invalid');
      }
      
      return updatedFilters;
    });
    
    setPreviousYearAlertFilters(prevFilters => {
      let updatedFilters = [...prevFilters];
      
      if (previousYearAlerts.errors === 0) {
        updatedFilters = updatedFilters.filter(f => f !== 'Error');
      }
      if (previousYearAlerts.warnings === 0) {
        updatedFilters = updatedFilters.filter(f => f !== 'Warning');
      }
      if (previousYearAlerts.invalids === 0) {
        updatedFilters = updatedFilters.filter(f => f !== 'Invalid');
      }
      
      return updatedFilters;
    });
  }, [currentYearAlerts, previousYearAlerts]);
  
  // Reset elevated state when all invalid fields are resolved
  useEffect(() => {
    const hasInvalidFields = Object.values(data.Row || {}).some(employee => 
      Object.values(employee).some(field => field?.FinalValue?.Status === 'Invalid')
    ) || Object.values(oldData.Row || {}).some(employee => 
      Object.values(employee).some(field => field?.FinalValue?.Status === 'Invalid')
    );
    
    if (!hasInvalidFields && elevateRequiredFields) {
      setElevateRequiredFields(false);
    }
  }, [data, oldData, elevateRequiredFields]);
  
  // Trigger save when requested
  useEffect(() => {
    if (saveRequest) { 
      SaveData();
      setSaveRequest(false);
    }
  }, [saveRequest, data, oldData]);

  const HandleSetCurrentYearSearchTerm = (term) => {
    if (currentPage != 1) {
      setCurrentPage(1);
    }
    setCurrentYearSearchTerm(term);
  }

  const HandleSetPreviousYearSearchTerm = (term) => {
    if (oldCurrentPage != 1) {
      setOldCurrentPage(1);
    }
    setPreviousYearSearchTerm(term);
  }

  // Saves current data state to backend
  const SaveData = async () => {
    setIsLoading(true);
    try {
      const projectId = currentProject;
      
      const reportPeriodStartDate = (reportStartDates && reportStartDates.length > 0)
        ? reportStartDates.map(date => ({
            Value: date.Value,
            Source: date.Source
          }))
        : [];
    
      const reportPeriodEndDate = (reportEndDates && reportEndDates.length > 0)
        ? reportEndDates.map(date => ({
            Value: date.Value,
            Source: date.Source
          }))
        : [];
    
      const mergedRowData = { ...data.Row, ...oldData.Row };
      
      const reconstructedData = {
        ReportPeriodStartDate: reportPeriodStartDate,
        ReportPeriodEndDate: reportPeriodEndDate,
        Row: mergedRowData
      };
      
      await saveData(projectId, reconstructedData, customerNumber);
      await fetchResults();
    } catch (error) {
      console.error('Failed to save data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Validates and submits final census data
  const SubmitFinalData = async () => {
    setIsLoading(true);
    
    const hasDataEmployees = data && data.Row && Object.keys(data.Row).length > 0;
    const hasOldDataEmployees = oldData && oldData.Row && Object.keys(oldData.Row).length > 0;
    
    if (!hasDataEmployees && !hasOldDataEmployees) {
      alert("Please add employees to the Current Year Census");
      setIsLoading(false);
      return;
    }
    
    try {
      const reportPeriodStartDate = (reportStartDates && reportStartDates.length > 0)
        ? reportStartDates.map(date => ({
            Value: date.Value,
            Source: date.Source
          }))
        : [];
   
      const reportPeriodEndDate = (reportEndDates && reportEndDates.length > 0)
        ? reportEndDates.map(date => ({
            Value: date.Value,
            Source: date.Source
          }))
        : [];
   
      const rowData = {
        ...((data && data.Row) || {}),
        ...((oldData && oldData.Row) || {})
      };
   
      // Check for errors or missing required fields
      let hasErrorsOrMissing = false;
      Object.values(rowData).forEach(employee => {
        Object.values(employee).forEach(field => {
          if (field?.FinalValue?.Status === 'Error' || field?.FinalValue?.Status === 'Invalid') {
            hasErrorsOrMissing = true;
          }
        });
      });
   
      if (hasErrorsOrMissing) {
        alert('Please fix all errors and missing data before submitting.');
        
        setElevateRequiredFields(true);
        
        if (!currentYearAlertFilters.includes('Invalid')) {
          setCurrentYearAlertFilters(prev => [...prev, 'Invalid']);
        }
        
        const hasErrors = Object.values(rowData).some(employee => 
          Object.values(employee).some(field => field?.FinalValue?.Status === 'Error')
        );
        
        if (hasErrors && !currentYearAlertFilters.includes('Error')) {
          setCurrentYearAlertFilters(prev => [...prev, 'Error']);
        }
        
        const hasPreviousYearData = oldData && oldData.Row && Object.keys(oldData.Row).length > 0;
        
        if (hasPreviousYearData) {
          if (!previousYearAlertFilters.includes('Invalid')) {
            setPreviousYearAlertFilters(prev => [...prev, 'Invalid']);
          }
          
          if (hasErrors && !previousYearAlertFilters.includes('Error')) {
            setPreviousYearAlertFilters(prev => [...prev, 'Error']);
          }
        }
        
        setIsLoading(false);
        return;
      }
       
      const reconstructedData = {
        ReportPeriodStartDate: reportPeriodStartDate,
        ReportPeriodEndDate: reportPeriodEndDate,
        Row: rowData
      };
      
      const response = await submitFinalData(
        customerNumber, 
        censusYearData.current_year, 
        censusYearData.prior_year, 
        censusYearData.current_year_end_date, 
        censusYearData.current_year_start_date, 
        censusYearData.prior_year_start_date, 
        censusYearData.prior_year_end_date, 
        iFinanceId, 
        priorYearIFinanceId, 
        reconstructedData, 
        currentProject
      );

      if (response.success) {
        const baseUrl = getBaseUrl();
        window.location.href = `${baseUrl}/${iFinanceId}`;
      } else {
        console.warn('Validation errors:', response.errors);
        setValidationErrors(response.errors);
        setShowValidationModal(true);
      }
    } catch (error) {
      console.error('Failed to submit final data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteRowClick = async (employeeKey, isOldData) => {
    if (processingStatus.isProcessing) {
      if (checkProcessingStatusAndShowModal()) {
        return;
      }
    }

    const employeeData = isOldData ? oldData.Row[employeeKey] : data.Row[employeeKey];

    // Prevent deletion of prior year employees with prioryear hire date
    if (employeeData?.PriorYearEmployee?.FinalValue?.Value === "true") {
      const hireDateSource = employeeData?.DateOfHire?.FinalValue?.Source?.toLowerCase();
      if (hireDateSource === "prioryear") {
        alert("You cannot delete a prior year employee.");
        return;
      }
    }

    const isDuplicate = employeeKey.endsWith('_duplicate');
    const baseKey = isDuplicate ? employeeKey.replace('_duplicate', '') : employeeKey;
    const duplicateKey = isDuplicate ? employeeKey : `${employeeKey}_duplicate`;

    const hasMatchingEntries = (
      data.Row[baseKey] && 
      oldData.Row[duplicateKey]
    );

    setIsDeletingOldData(isOldData);
    setEmployeeKeyToDelete(employeeKey);
    setDeleteMessage(
      hasMatchingEntries 
        ? "Are you sure you want to delete this user? This will delete the employee from both tables."
        : "Are you sure you want to delete this user?"
    );
    setIsDeletingAll(false);
    setShowDeleteModal(true);
  };

  const handleDeleteProjectClick = async () => {
    setIsDeletingOldData(null);
    setEmployeeKeyToDelete(null);
    setDeleteMessage("Are you sure you want to delete everything submitted in the new year census?");
    setIsDeletingAll(true);
    setShowDeleteModal(true);
  }

  const handleConfirmDeleteProject = async () => {
    try {
      setIsLoading(true);
      const success = await archiveProject(currentProject, customerNumber);
  
      if (success) {
        setData([]);
        setOldData([]);
        window.location.reload();
      } else {
        alert('Failed to archive the project.');
      }
    
      setShowDeleteModal(false);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleConfirmDeleteRow = () => {
    const isDuplicate = employeeKeyToDelete.endsWith('_duplicate');
    const baseKey = isDuplicate 
      ? employeeKeyToDelete.replace('_duplicate', '')
      : employeeKeyToDelete;
  
    setOldData(prevOldData => {
      const newOldData = { ...prevOldData };
      delete newOldData.Row[employeeKeyToDelete];
      return newOldData;
    });
  
    setData(prevData => {
      const newData = { ...prevData };
      delete newData.Row[baseKey];
      return newData;
    });
  
    setShowDeleteModal(false);
    setSaveRequest(true);
  };
  
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const handlePageChange = (pageNumber, isOldData) => {
    isOldData ? setOldCurrentPage(pageNumber) : setCurrentPage(pageNumber);
  }
  
  const handleResolveClick = async (values, employeeKey, field, isOldData) => {
    if (processingStatus.isProcessing) {
      if (checkProcessingStatusAndShowModal()) {
        return;
      }
    }

    const dataSource = isOldData ? oldData.Row : data.Row;
    const employeeData = dataSource[employeeKey];

    let actualField = field;
    let conflicts = [];

    if (field === 'rehire') {
      setIsRehireConflict(true);
      actualField = 'DateOfHire';
    } else {
      setIsRehireConflict(false);
    }

    // Handle FullName conflicts by combining first and last name values
    if (actualField === 'FullName') {
      const firstNameValues = dataSource[employeeKey].FirstName.Values;
      const lastNameValues = dataSource[employeeKey].LastName.Values;

      if (firstNameValues.length > 0 && lastNameValues.length > 0) {
        for (let i = 0; i < firstNameValues.length; i++) {
          for (let j = 0; j < lastNameValues.length; j++) {
            conflicts.push({
              firstName: firstNameValues[i],
              lastName: lastNameValues[j],
              employeeKey,
              field: actualField,
              isOldData
            });
          }
        }
      } else if (firstNameValues.length > 0) {
        conflicts = firstNameValues.map(firstName => ({
          firstName,
          lastName: { Value: '', Source: '' },
          employeeKey,
          field: actualField,
          isOldData
        }));
      } else if (lastNameValues.length > 0) {
        conflicts = lastNameValues.map(lastName => ({
          firstName: { Value: '', Source: '' },
          lastName,
          employeeKey,
          field: actualField,
          isOldData
        }));
      }
    } else {
      conflicts = values.map(value => ({
        ...value,
        employeeKey,
        field: actualField,
        isOldData
      }));
    }

    const conflictData = {
      employeeData,
      conflicts
    };

    setConflictValues(conflictData);
    setShowConflictModal(true);
  };

  const handleBulkResolveClick = (field, isOldData) => {
    const dataToCheck = isOldData ? oldData : data;
    const conflictingRows = [];
  
    Object.entries(dataToCheck.Row).forEach(([key, row]) => {
      if (row[field]?.FinalValue?.Value === "conflict") {
        conflictingRows.push(row);
      }
    });
  
    // Aggregate unique sources and their example values
    const sourceValuesMap = new Map();
  
    conflictingRows.forEach(row => {
      row[field].Values.forEach(valueObj => {
        const source = valueObj.Source;
        const value = valueObj.Value;
  
        if (!sourceValuesMap.has(source)) {
          sourceValuesMap.set(source, value);
        }
      });
    });
  
    const bulkConflictSourcesArray = Array.from(sourceValuesMap.entries()).map(
      ([source, value]) => ({ Source: source, Value: value })
    );
  
    setBulkConflictSources(bulkConflictSourcesArray);
    setBulkConflictField(field);
    setIsOldDataBulkResolve(isOldData);
    setShowBulkConflictModal(true);
  };

  const handleBulkConflictChange = (selectedSource) => {
    const dataToUpdate = isOldDataBulkResolve ? { ...oldData } : { ...data };
  
    Object.entries(dataToUpdate.Row).forEach(([key, row]) => {
      if (row[bulkConflictField]?.FinalValue?.Value === 'conflict') {
        const fieldValues = row[bulkConflictField].Values;
        const selectedSourceValues = fieldValues.filter(value => value.Source === selectedSource);
  
        // Only resolve if source appears exactly once
        if (selectedSourceValues.length === 1) {
          const selectedValue = selectedSourceValues[0];
          row[bulkConflictField].FinalValue = { 
            ...selectedValue, 
            Status: 'Valid', 
            Message: 'Conflict resolved.' 
          };
        }
      }
    });
  
    if (isOldDataBulkResolve) {
      setOldData(dataToUpdate);
    } else {
      setData(dataToUpdate);
    }
    setSaveRequest(true);
  };
  
  const handleZeroHourClick = async (employeeKey, isOldData, hoursFinalValue, grossCompensation, hoursWorkedNoteFinalValue, isOwner) => {
    if (processingStatus.isProcessing) {
      if (checkProcessingStatusAndShowModal()) {
        return;
      }
    }
    
    const employeeData = isOldData ? oldData.Row[employeeKey] : data.Row[employeeKey];
    
    const firstName = employeeData?.FirstName?.FinalValue?.Value || '';
    const lastName = employeeData?.LastName?.FinalValue?.Value || '';
    const fullName = `${firstName} ${lastName}`.trim();
    
    setHoursGrossCompensation(grossCompensation);
    setSelectedEmployeeFullName(fullName);
    setSelectedEmployeeData(employeeData);
    setShowHoursModal(true);
    setIsOldDataSaving(isOldData);
    setSelectedEmployeeKey(employeeKey);
    setHoursFinalValue(hoursFinalValue);
    setHoursWorkedNoteFinalValue(hoursWorkedNoteFinalValue);
    setHoursIsOwner(isOwner);
  };

  const handleAddRow = async (isOldData) => {
    if (processingStatus.isProcessing) {
      if (checkProcessingStatusAndShowModal()) {
        return;
      }
    }
    
    setEditableRowIndex(null);
    setEditableOldRowIndex(null);
    
    if (isOldData) {
      setIsAddingOldNewRow(true);
      setIsAddingNewRow(false); 
      
      const newRowData = {
        ...initialEditableRowData,
        originalKey: null
      };
      
      setEditableOldRowData(newRowData);
      
      if (!oldData || Object.keys(oldData).length === 0) {
        setOldData([]);
      }
    } else {
      setIsAddingNewRow(true);
      setIsAddingOldNewRow(false);
      
      const newRowData = {
        ...initialEditableRowData,
        originalKey: null
      };
      
      setEditableRowData(newRowData);
      
      if (!data || Object.keys(data).length === 0) {
        setData([]);
      }
    }
  };

  const handleEditRow = async (key, isOldData) => {
    if (processingStatus.isProcessing) {
      if (checkProcessingStatusAndShowModal()) {
        return;
      }
    }
  
    setIsAddingNewRow(false);
    setIsAddingOldNewRow(false);
  
    const dataToEdit = isOldData ? oldData : data;
    const editableData = isOldData ? setEditableOldRowData : setEditableRowData;
    const setEditableIndex = isOldData ? setEditableOldRowIndex : setEditableRowIndex;
  
    if (isOldData) {
      setEditableRowIndex(null);
    } else {
      setEditableOldRowIndex(null);
    }
  
    setEditableIndex(key);
    const employee = dataToEdit.Row[key];
    if (!employee) {
      console.error('Employee data is undefined');
      return;
    }

    setOriginalRowData(employee);
    editableData({
      originalKey: key,
      Id: key,
      FullName: `${employee.FirstName?.FinalValue?.Value || ''} ${employee.MiddleName?.FinalValue?.Value || ''} ${employee.LastName?.FinalValue?.Value || ''}`.trim(),
      FirstName: employee.FirstName?.FinalValue?.Value || '',
      LastName: employee.LastName?.FinalValue?.Value || '',
      MiddleName: employee.MiddleName?.FinalValue?.Value || '',
      SSN: employee.SSN?.FinalValue?.Value || '',
      DateOfBirth: employee.DateOfBirth?.FinalValue?.Value || '',
      DateOfHire: employee.DateOfHire?.FinalValue?.Value || '',
      DateOfReHire: employee.DateOfReHire?.FinalValue?.Value || '',
      DateOfTermination: employee.DateOfTermination?.FinalValue?.Value || '',
      Status: employee.Status?.FinalValue?.Value || '',
      GrossCompensation: employee.GrossCompensation?.FinalValue?.Value || '',
      HoursWorked: employee.HoursWorked?.FinalValue?.Value || '',
      HoursWorkedNote: employee.HoursWorkedNote?.FinalValue?.Value || '',
      DeferALS: employee.DeferALS?.FinalValue?.Value || '',
      ParticipantRoth: employee.ParticipantRoth?.FinalValue?.Value || '',
      CompanyMatch: employee.CompanyMatch?.FinalValue?.Value || '',
      CompanyProfitSharing: employee.CompanyProfitSharing?.FinalValue?.Value || '',
      AnnualLoanPayment: employee.AnnualLoanPayment?.FinalValue?.Value || '',
      RollOver: employee.RollOver?.FinalValue?.Value || '',
      TypeOfStatusChange: employee.TypeOfStatusChange?.FinalValue?.Value || 'Active',
      EndDate: employee.DateOfStatusChange?.FinalValue?.Value || '',
      PriorYearEmployee: employee.PriorYearEmployee?.FinalValue?.Value || ''
    });
  };

  const handleCalculateGuidantHours = (employeeKey) => {
    const updatedData = isOldDataSaving ? { ...oldData } : { ...data };
    const employee = updatedData.Row[employeeKey];
    
    const dateOfHire = employee.DateOfHire?.FinalValue?.Value;
    const dateOfTermination = employee.DateOfTermination?.FinalValue?.Value;
    
    const estimatedHours = calculateGuidantEstimatedHours(
      dateOfHire,
      dateOfTermination,
      censusYearData.current_year_start_date,
      censusYearData.current_year_end_date
    );
    
    employee.HoursWorked = {
      Values: [{ Value: estimatedHours, Source: 'guidant_estimate' }],
      FinalValue: { 
        Value: estimatedHours, 
        Source: 'guidant_estimate',
        Status: 'Valid',
        Message: ''
      }
    };
    
    employee.HoursWorkedNote = {
      Values: [{ Value: 'Hours estimated based on employment dates', Source: 'guidant_estimate' }],
      FinalValue: { 
        Value: 'Hours estimated based on employment dates', 
        Source: 'guidant_estimate',
        Status: '',
        Message: ''
      }
    };
    
    isOldDataSaving ? setOldData(updatedData) : setData(updatedData);
    setSaveRequest(true);
  };

  const handleUpdateTermination = (employeeKey, terminationDate) => {
    const updatedData = isOldDataSaving ? { ...oldData } : { ...data };
    const employee = updatedData.Row[employeeKey];
    
    if (terminationDate) {
      const [year, month, day] = terminationDate.split('-');
      const formattedDate = `${month}-${day}-${year}`;
      
      employee.DateOfTermination = {
        Values: [{ Value: formattedDate, Source: 'manual_input' }],
        FinalValue: { 
          Value: formattedDate, 
          Source: 'manual_input',
          Status: 'Valid',
          Message: ''
        }
      };
      
      employee.Status = {
        Values: [{ Value: 'Terminated', Source: 'manual_input' }],
        FinalValue: { 
          Value: 'Terminated', 
          Source: 'manual_input',
          Status: 'Valid',
          Message: ''
        }
      };
    } else {
      employee.DateOfTermination = {
        Values: [{ Value: '', Source: 'manual_input' }],
        FinalValue: { 
          Value: '', 
          Source: 'manual_input',
          Status: '',
          Message: ''
        }
      };
      
      employee.Status = {
        Values: [{ Value: 'Active', Source: 'manual_input' }],
        FinalValue: { 
          Value: 'Active', 
          Source: 'manual_input',
          Status: 'Valid',
          Message: ''
        }
      };
    }
    
    isOldDataSaving ? setOldData(updatedData) : setData(updatedData);
  };

  // Creates or updates field data preserving conflicts and prior year data
  const createOrUpdateField = (field, value, existingData, oldKey, censusYearData, defaultFieldData) => {
    let existingField = existingData?.Row[oldKey]?.[field];

    if (field === 'PriorYearEmployee' && existingField?.FinalValue?.Source === 'PriorYear') {
      return existingField;
    }

    if (field === 'Status') {
      if (!existingField || !existingField.Values) {
        existingField = defaultFieldData();
      }
      
      let manualInputIndex = existingField.Values.findIndex(v => 
        v.Source === 'manual_input'
      );

      if (manualInputIndex === -1) {
        existingField.Values.push({ Value: value, Source: 'manual_input' });
      } else {
        existingField.Values[manualInputIndex].Value = value;
      }

      existingField.FinalValue = {
        Value: value,
        Source: 'manual_input',
        Status: 'updated',
        Message: 'Value updated'
      };

      return existingField;
    }

    if (existingField?.FinalValue?.Value === 'conflict') {
      return existingField;
    }

    if (!existingField || !existingField.Values) {
      existingField = defaultFieldData();
    }

    // Handle rehire scenarios
    if (field === 'DateOfHire') {
      const priorYearValue = existingField.Values?.find(v => 
        v.Source?.toLowerCase() === 'prioryear'
      );

      const terminationDate = existingData?.Row[oldKey]?.DateOfTermination?.FinalValue;
      const hasTerminationFromPriorYear = terminationDate?.Value && 
        terminationDate.Source?.toLowerCase() === 'prioryear' &&
        new Date(terminationDate.Value) < new Date(censusYearData.current_year_start_date);

      if (priorYearValue && hasTerminationFromPriorYear) {
        existingData.Row[oldKey].DateOfTermination.FinalValue = {
          Value: "",
          Source: "manual_input",
          Status: "updated",
          Message: "Cleared termination due to rehire"
        };
        
        existingData.Row[oldKey].Status.FinalValue = {
          Value: "Active",
          Source: "manual_input",
          Status: "updated",
          Message: "Status updated due to rehire"
        };
        
        if (new Date(value) > new Date(terminationDate.Value)) {
          existingData.Row[oldKey].DateOfRehire = {
            Values: [{ Value: value, Source: 'manual_input' }],
            FinalValue: {
              Value: value,
              Source: 'manual_input',
              Status: 'updated',
              Message: 'Rehire date added'
            }
          };
          
          return {
            Values: [
              priorYearValue,
              { Value: value, Source: 'manual_input' }
            ],
            FinalValue: {
              Value: value,
              Source: 'manual_input',
              Status: 'Valid',
              Message: 'Rehire date set'
            }
          };
        } else {
          return {
            Values: [
              priorYearValue,
              { Value: value, Source: 'manual_input' }
            ],
            FinalValue: {
              Value: 'rehire',
              Source: 'manual_input',
              Status: 'Error', 
              Message: 'Rehire detected - needs resolution'
            }
          };
        }
      }
    }

    const originalValue = existingField?.FinalValue?.Value;
    const originalSource = existingField?.FinalValue?.Source;
    const numericFields = ['HoursWorked', 'GrossCompensation', 'DeferALS', 'ParticipantRoth', 'CompanyMatch', 'CompanyProfitSharing', 'AnnualLoanPayment', 'RollOver'];

    let compareOriginalValue = originalValue;
    let compareNewValue = value;
    
    if (numericFields.includes(field)) {
      if (originalValue && originalValue !== '') {
        compareOriginalValue = parseFloat(originalValue).toFixed(2);
      }
      if (value && value !== '') {
        compareNewValue = parseFloat(value).toFixed(2);
      }
    }

    if (compareNewValue !== compareOriginalValue) {
      if (value?.trim?.() !== '' || field === 'DateOfTermination' || numericFields.includes(field)) {
        let manualInputIndex = existingField.Values.findIndex(v => 
          v.Source === 'manual_input'
        );

        if (manualInputIndex === -1) {
          existingField.Values.push({ Value: value, Source: 'manual_input' });
        } else {
          existingField.Values[manualInputIndex].Value = value;
        }
        
        const terminationDate = existingData?.Row[oldKey]?.DateOfTermination?.FinalValue;
        const hasTerminationFromPriorYear = terminationDate?.Value && 
          terminationDate.Source?.toLowerCase() === 'prioryear' &&
          new Date(terminationDate.Value) < new Date(censusYearData.current_year_start_date);
          
        if (hasTerminationFromPriorYear && originalValue !== value && value?.trim?.() !== '') {
          if (originalValue && originalValue.trim() !== '' && originalSource.toLowerCase() == 'prioryear') {
            existingField.FinalValue = {
              Value: 'conflict',
              Source: 'manual_input',
              Status: 'Error',
              Message: ``
            };
          } else {
            existingField.FinalValue = {
              Value: value,
              Source: 'manual_input',
              Status: 'updated',
              Message: 'Value updated'
            };
          }
        } else {
          existingField.FinalValue = {
            Value: value,
            Source: 'manual_input',
            Status: 'updated',
            Message: 'Value updated'
          };
        }
      } else {
        if (originalValue && originalValue.trim() !== '' && field !== 'DateOfTermination') {
          // Keep existing value if clearing a non-termination field
        } else {
          existingField.FinalValue = {
            Value: value,
            Source: 'manual_input',
            Status: '',
            Message: ''
          };
        }
      }
    }

    return existingField;
  };

  const ensureAllFields = (newEntry, oldKey, dataToUpdate) => {
    const allFields = [
      'PriorYearEmployee', 
      'FirstName', 
      'LastName', 
      'MiddleName', 
      'OwnershipPercent', 
      'SSN',
      'DateOfBirth', 
      'DateOfHire', 
      'DateOfReHire', 
      'DateOfTermination', 
      'Status', 
      'GrossCompensation',
      'GrossCompensationNote', 
      'HoursWorked', 
      'HoursWorkedNote', 
      'DeferALS', 
      'ParticipantRoth', 
      'CompanyMatch', 
      'CompanyProfitSharing', 
      'AnnualLoanPayment', 
      'RollOver'
    ];
    
    allFields.forEach(field => {
      if (!newEntry[field]) {
        if (field === 'PriorYearEmployee' && 
            oldKey && 
            dataToUpdate.Row[oldKey]?.[field]?.FinalValue?.Source === 'PriorYear') {
            newEntry[field] = dataToUpdate.Row[oldKey][field];
        } else {
            newEntry[field] = defaultFieldData();
        }
      }
    });
  };

  const determineTargetTable = (DateOfTermination, censusYearData, data, oldData, setData, setOldData) => {
    const endDateYear = new Date(DateOfTermination).getFullYear();
    const censusYear = parseInt(censusYearData.current_year);
    const isCurrentYear = isNaN(endDateYear) || endDateYear >= censusYear;
    
    return {
      targetData: isCurrentYear ? data : oldData,
      setTargetData: isCurrentYear ? setData : setOldData,
      removeFromData: isCurrentYear ? setOldData : setData
    };
  };

  const clearPriorYearFinancialFields = (newEntry, DateOfTermination, censusYearData) => {
    const newTermDate = DateOfTermination;
    const isCleared = !newTermDate || newTermDate.trim() === '';
    const isInCurrentYear = newTermDate && 
      new Date(newTermDate).getFullYear() === parseInt(censusYearData.current_year);
    
    if (isCleared || isInCurrentYear) {
      const financialFields = [
        'DeferALS', 
        'ParticipantRoth', 
        'CompanyMatch', 
        'CompanyProfitSharing', 
        'AnnualLoanPayment', 
        'RollOver'
      ];
      
      financialFields.forEach(field => {
        if (newEntry[field]?.FinalValue?.Source?.toLowerCase() === 'prioryear') {
          newEntry[field].FinalValue = {
            Value: '',
            Source: '',
            Status: '',
            Message: ''
          };
        }
      });
    }
  };

  const findExistingEmployee = (editableData, data, oldData) => {
    let existingEmployee = null;
    let existingData = null;
    const newSSN = editableData.SSN;
    
    if (editableData.originalKey) {
      if (data?.Row?.[editableData.originalKey]) {
        existingEmployee = data.Row[editableData.originalKey];
        existingData = data;
      } else if (oldData?.Row?.[editableData.originalKey]) {
        existingEmployee = oldData.Row[editableData.originalKey];
        existingData = oldData;
      }
    } else {
      if (data?.Row && newSSN && newSSN.trim() !== '') {
        const existingKey = Object.keys(data.Row).find(key => 
          data.Row[key].SSN?.FinalValue?.Value === newSSN
        );
        if (existingKey) {
          existingEmployee = data.Row[existingKey];
          existingData = data;
          editableData.originalKey = existingKey;
        }
      }
    
      if (!existingEmployee && oldData?.Row && newSSN && newSSN.trim() !== '') {
        const existingKey = Object.keys(oldData.Row).find(key => 
          oldData.Row[key].SSN?.FinalValue?.Value === newSSN
        );
        if (existingKey) {
          existingEmployee = oldData.Row[existingKey];
          existingData = oldData;
          editableData.originalKey = existingKey;
        }
      }
    }
    
    return { existingEmployee, existingData };
  };

  const handleSaveRow = async (isOldData) => {
    const dataToUpdate = isOldData ? oldData : data;
    const editableData = isOldData ? editableOldRowData : editableRowData;
    
    // Find existing employee
    const { existingEmployee, existingData } = findExistingEmployee(editableData, data, oldData);

    // Normalize all fields
    const normalizedData = normalizeEmployeeFields(editableData);
    
    let {
      FirstName,
      LastName,
      MiddleName,
      DateOfBirth,
      DateOfHire,
      DateOfReHire,
      DateOfTermination,
      Status,
      GrossCompensation,
      GrossCompensationNote,
      HoursWorked,
      HoursWorkedNote,
      DeferALS,
      ParticipantRoth,
      CompanyMatch,
      CompanyProfitSharing,
      AnnualLoanPayment,
      RollOver,
      SSN,
      originalKey: oldKey
    } = normalizedData;

    // Validate required fields
    if (!FirstName?.trim()) {
      alert("Please enter a first name");
      return;
    }
    
    if (!LastName?.trim()) {
      alert("Please enter a last name");
      return;
    }
    
    // Generate employee key
    const newKey = await generateEmployeeKey(
      existingEmployee, 
      oldKey, 
      FirstName, 
      LastName, 
      DateOfBirth, 
      SSN, 
      customerNumber
    );
    
    if (!dataToUpdate.Row) {
      dataToUpdate.Row = {};
    }

    // Build fields object
    const fields = {
      FirstName, 
      LastName, 
      MiddleName, 
      DateOfBirth, 
      DateOfHire, 
      DateOfReHire, 
      DateOfTermination,
      Status, 
      GrossCompensation, 
      GrossCompensationNote, 
      HoursWorked, 
      HoursWorkedNote, 
      DeferALS,
      ParticipantRoth, 
      CompanyMatch, 
      CompanyProfitSharing, 
      AnnualLoanPayment, 
      RollOver, 
      SSN
    };

    // Create new entry with field updates
    const newEntry = {};
    for (const [field, value] of Object.entries(fields)) {
      newEntry[field] = createOrUpdateField(
        field, 
        value || '', 
        existingData, 
        oldKey, 
        censusYearData, 
        defaultFieldData
      );
    }

    // Clear financial fields when termination is cleared or in current year
    if (existingEmployee) {
      clearPriorYearFinancialFields(newEntry, DateOfTermination, censusYearData);
    }

    // Preserve existing fields
    if (existingEmployee && oldKey) {
      Object.keys(existingEmployee).forEach(key => {
        if (!newEntry.hasOwnProperty(key) || 
            (key === 'PriorYearEmployee' && existingEmployee[key]?.FinalValue?.Source === 'PriorYear')) {
            newEntry[key] = existingEmployee[key];
        }
      });
    }

    // Clear hours note when editing Guidant estimated hours
    if (oldKey && 
        dataToUpdate.Row[oldKey]?.HoursWorked?.Values?.some(v => v.Source === 'guidant_estimate')) {
      
      // Get original hours value and compare numerically
      const originalHours = dataToUpdate.Row[oldKey]?.HoursWorked?.FinalValue?.Value;
      const originalAsNumber = parseFloat(originalHours);
      const newAsNumber = parseFloat(HoursWorked);
      
      // Only clear if values are actually different numerically
      if (!isNaN(originalAsNumber) && !isNaN(newAsNumber) && originalAsNumber !== newAsNumber) {
        newEntry.HoursWorkedNote = {
          Values: [{ Value: '', Source: 'manual_input' }],
          FinalValue: { Value: '', Source: 'manual_input', Status: '', Message: '' }
        };
      }
    }

    // Ensure all fields have default values
    ensureAllFields(newEntry, oldKey, dataToUpdate);
    
    newEntry['Id'] = newKey;

    if (oldKey) {
      delete dataToUpdate.Row[oldKey];
    }

    // Determine target table and update data
    const { targetData, setTargetData, removeFromData } = determineTargetTable(
      DateOfTermination, 
      censusYearData, 
      data, 
      oldData, 
      setData, 
      setOldData
    );

    if (!targetData?.Row) {
      targetData.Row = {};
    }
    targetData.Row[newKey] = newEntry;

    setTargetData({ ...targetData });
    removeFromData(prevData => {
      const newData = { ...prevData };
      delete newData.Row[newKey];
      return newData;
    });

    // Reset editing state
    isOldData ? setIsAddingOldNewRow(false) : setIsAddingNewRow(false);
    isOldData ? setEditableOldRowIndex(null) : setEditableRowIndex(null);

    if (isOldData) {
      setEditableOldRowData({...initialEditableRowData, originalKey: null});
    } else {
      setEditableRowData({...initialEditableRowData, originalKey: null});
    }

    setSaveRequest(true);
  };

  const handleSaveHoursWorked = (employeeKey, hoursWorked, description, source = 'manual_input') => {
    const updatedData = isOldDataSaving ? { ...oldData } : { ...data };
    const employee = updatedData.Row[selectedEmployeeKey];
    
    if (!employee.HoursWorked) {
      employee.HoursWorked = {
        Values: [],
        FinalValue: {}
      };
    }
  
    if (!employee.HoursWorkedNote) {
      employee.HoursWorkedNote = {
        Values: [],
        FinalValue: {}
      };
    }
  
    const newHoursWorkedValue = { Value: hoursWorked, Source: source };
    const newHoursWorkedNoteValue = { Value: description, Source: source };
  
    employee.HoursWorked.Values.push(newHoursWorkedValue);
    employee.HoursWorked.FinalValue = { ...newHoursWorkedValue, Status: 'Valid', Message: '' };
  
    employee.HoursWorkedNote.Values.push(newHoursWorkedNoteValue);
    employee.HoursWorkedNote.FinalValue = { ...newHoursWorkedNoteValue, Status: '', Message: '' };
  
    isOldDataSaving ? setOldData(updatedData) : setData(updatedData);
    setSaveRequest(true);
  };

  const handleInputChange = (field, value, isOldData) => {
    const setEditableData = isOldData ? setEditableOldRowData : setEditableRowData;
    
    // Convert date format for display
    if (['DateOfHire', 'DateOfBirth', 'DateOfTermination'].includes(field) && value) {
      const [year, month, day] = value.split('-');
      value = `${month}/${day}/${year}`;
    }
    
    setEditableData(prevData => ({ ...prevData, [field]: value }));
  };
  
  const handleConflictChange = (selectedConflict) => {
    const { employeeKey, field, Value, Source, isRehireConflict } = selectedConflict;
    
    const newData = JSON.parse(JSON.stringify(data));
    const newOldData = JSON.parse(JSON.stringify(oldData));
    
    const updateEmployeeRecord = (dataObj, key) => {
      if (dataObj.Row[key]) {
        if (field === 'FullName') {
          const { firstName, lastName } = selectedConflict;
          if (dataObj.Row[key].FirstName) {
            dataObj.Row[key].FirstName.FinalValue = { 
              Value: firstName.Value, 
              Source: firstName.Source, 
              Status: "Valid", 
              Message: "First Name conflict resolved." 
            };
          }
          if (dataObj.Row[key].LastName) {
            dataObj.Row[key].LastName.FinalValue = { 
              Value: lastName.Value, 
              Source: lastName.Source, 
              Status: "Valid", 
              Message: "Last Name conflict resolved." 
            };
          }
        } else if (dataObj.Row[key][field]) {
          dataObj.Row[key][field].FinalValue = { 
            Value, 
            Source, 
            Status: "Valid", 
            Message: `${field} conflict resolved.` 
          };

          if (isRehireConflict && dataObj.Row[key].DateOfReHire) {
            const existingTerminationDate = dataObj.Row[key].DateOfTermination?.FinalValue?.Value;
            const newDate = new Date(Value);
            const terminationDate = existingTerminationDate ? new Date(existingTerminationDate) : null;

            dataObj.Row[key].DateOfReHire.FinalValue = {
              Value,
              Source,
              Status: "Valid",
              Message: "Date of rehire updated."
            };

            if (terminationDate && newDate > terminationDate) {
              dataObj.Row[key].DateOfTermination.FinalValue = {
                Value: "",
                Source: "",
                Status: "",
                Message: ""
              };
              
              dataObj.Row[key].Status.FinalValue = {
                Value: "Active",
                Source,
                Status: "Valid",
                Message: "Date of rehire updated."
              };
            } else if (terminationDate) {
              dataObj.Row[key].Status.FinalValue = {
                Value: "Terminated",
                Source,
                Status: "Valid",
                Message: "Employee terminated after hire date."
              };

              if (dataObj.Row[key].GrossCompensation?.FinalValue) {
                dataObj.Row[key].GrossCompensation.FinalValue.Status = "Valid";
              }
              if (dataObj.Row[key].HoursWorked?.FinalValue) {
                dataObj.Row[key].HoursWorked.FinalValue.Status = "Valid";
              }
            }
          }
        }
      }

      setEditableRowIndex(null);
      setIsAddingNewRow(false);
      setEditableRowData(initialEditableRowData);
    };
  
    const mainKey = employeeKey.replace('_duplicate', '');
  
    [newData, newOldData].forEach(dataObj => {
      updateEmployeeRecord(dataObj, employeeKey);
      updateEmployeeRecord(dataObj, mainKey);
    });
  
    setData(newData);
    setOldData(newOldData);
    setSaveRequest(true);
  };
  
  const handleAlertClick = (filter, isOldData) => {
    handlePageChange(1, isOldData);
    
    // Clear all filters when Error filter is turned off
    if ((filter === 'Error' && currentYearAlertFilters.includes('Error')) || 
        (filter === 'Error' && previousYearAlertFilters.includes('Error'))) {
      setCurrentYearAlertFilters([]);
      setPreviousYearAlertFilters([]);
      return;
    }
    
    if (isOldData) {
      setPreviousYearAlertFilters(prevFilters => {
        const newFilters = prevFilters.includes(filter)
          ? prevFilters.filter(f => f !== filter)
          : [...prevFilters, filter];
        return newFilters;
      });
    } else {
      setCurrentYearAlertFilters(prevFilters => {
        const newFilters = prevFilters.includes(filter)
          ? prevFilters.filter(f => f !== filter)
          : [...prevFilters, filter];
        return newFilters;
      });
    }
  };

  const filteredData = filterData(data, currentYearSearchTerm, currentYearAlertFilters, censusYearData);
  const oldFilteredData = filterData(oldData, previousYearSearchTerm, previousYearAlertFilters, censusYearData);
  
  const priorCensusTitle = `PAST YEAR CENSUS CHANGES (${formatDate(censusYearData?.prior_year_start_date)} - ${formatDate(censusYearData?.prior_year_end_date)})`;

  return (
    <div className={`results-content-container ${isSidebarVisible ? 'with-sidebar' : 'no-sidebar'}`}>
      {isSidebarVisible && <Sidebar sources={sources} setCurrentView={setCurrentView} toggleSidebar={toggleSidebar} />}
      <div className="results-content-wrapper">
        <div className="content-wrapper">
          <MainContentHeader
            start_date={censusYearData?.current_year_start_date ?? ''}
            end_date={censusYearData?.current_year_end_date ?? ''}
            subtitle="Submit Employee and Owner Information"
            description="The table displays employees and owners that were active at the beginning of the plan year. To complete your census, add new employees, verify and update statuses, and provide payroll details. Resolve any errors and review warnings to proceed to the next step."
            currentView={currentView}
          />
          <div className="buttons">
            <button className="button-add-manually" onClick={() => handleAddRow(false)}>
              <img src={addManuallyIcon} alt="Add Manually" />
              ADD MANUALLY
            </button>
            <button className="button-upload-file" onClick={() => setCurrentView('upload')}>
              <img src={uploadFileIcon} alt="Upload File" />
              UPLOAD FILE
            </button>
          </div>
          {processingStatus.isProcessing && (
            <div className="processing-warning" onClick={() => checkProcessingStatusAndShowModal()}>
              <span className="processing-warning-icon">
                <i className="fas fa-info-circle"></i>
              </span>
              Editing the table is unavailable while documents are processing. Click to learn more.
            </div>
          )}
          <ResultsTableSection
            title="CURRENT YEAR CENSUS"
            data={filteredData}
            currentPage={currentPage}
            rowsPerPage={8}
            isAddingNewRow={isAddingNewRow}
            editableRowIndex={editableRowIndex}
            editableRowData={editableRowData}
            handlePageChange={(pageNumber) => handlePageChange(pageNumber, false)}
            handleEditRow={(key) => handleEditRow(key, false)}
            handleSaveRow={() => handleSaveRow(false)}
            handleInputChange={(field, value) => handleInputChange(field, value, false)}
            handleResolveClick={(values, employeeKey, field) => handleResolveClick(values, employeeKey, field, false)}
            handleBulkResolveClick={(field) => handleBulkResolveClick(field, false)}
            handleZeroHourClick={(employeeKey, isOldData, hoursFinalValue, grossCompensation, hoursWorkedNoteFinalValue, isOwner) => handleZeroHourClick(employeeKey, false, hoursFinalValue, grossCompensation, hoursWorkedNoteFinalValue, isOwner)}
            initialEditableRowData={initialEditableRowData}
            setIsAddingNewRow={setIsAddingNewRow}
            setEditableRowIndex={setEditableRowIndex}
            setEditableRowData={setEditableRowData}
            setCurrentView={setCurrentView}
            isOldData={false}
            showTable={showCurrentYearTable}
            setShowTable={setShowCurrentYearTable}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            searchTerm={currentYearSearchTerm}
            setSearchTerm={(term) => HandleSetCurrentYearSearchTerm(term)}
            handleAddRow={() => handleAddRow(false)}
            alertFilters={currentYearAlertFilters}
            handleAlertClick={(filter) => handleAlertClick(filter, false)}
            setData={setData}
            setOldData={setOldData}
            handleDeleteRowClick={handleDeleteRowClick}
            alerts={currentYearAlerts}
            elevateRequiredFields={elevateRequiredFields}
          />
          {(Object.keys(oldFilteredData.Row || {}).length > 0 || (previousYearAlertFilters && previousYearAlertFilters.length > 0) || previousYearSearchTerm) && (
            <>
              <div className="divider"></div>
              <ResultsTableSection
                title={priorCensusTitle}
                data={oldFilteredData}
                currentPage={oldCurrentPage}
                rowsPerPage={8}
                isAddingNewRow={isAddingOldNewRow}
                editableRowIndex={editableOldRowIndex}
                editableRowData={editableOldRowData}
                handlePageChange={(pageNumber) => handlePageChange(pageNumber, true)}
                handleEditRow={(key) => handleEditRow(key, true)}
                handleSaveRow={() => handleSaveRow(true)}
                handleInputChange={(field, value) => handleInputChange(field, value, true)}
                handleResolveClick={(values, employeeKey, field) => handleResolveClick(values, employeeKey, field, true)}
                handleBulkResolveClick={(field) => handleBulkResolveClick(field, true)}
                handleZeroHourClick={(employeeKey, isOldData, hoursFinalValue, grossCompensation, hoursWorkedNoteFinalValue, isOwner) => handleZeroHourClick(employeeKey, true, hoursFinalValue, grossCompensation, hoursWorkedNoteFinalValue, isOwner)}
                initialEditableRowData={initialEditableRowData}
                setIsAddingNewRow={setIsAddingOldNewRow}
                setEditableRowIndex={setEditableOldRowIndex}
                setEditableRowData={setEditableOldRowData}
                setCurrentView={setCurrentView}
                isOldData={true}
                showTable={showPreviousYearTable}
                setShowTable={setShowPreviousYearTable}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                searchTerm={previousYearSearchTerm}
                setSearchTerm={(term) => HandleSetPreviousYearSearchTerm(term)}
                handleAddRow={() => handleAddRow(true)}
                alertFilters={previousYearAlertFilters}
                handleAlertClick={(filter) => handleAlertClick(filter, true)}
                setData={setData}
                setOldData={setOldData}
                handleDeleteRowClick={handleDeleteRowClick}
                alerts={previousYearAlerts}
                elevateRequiredFields={elevateRequiredFields}
              />
            </>
          )}
          <div className="save-button-container">
            <button className="delete-button" onClick={handleDeleteProjectClick}>
              <img src={whiteTrashIcon} alt="Delete" className="icon" />
              Start Over
            </button>
            <button 
              className="save-button" 
              onClick={SubmitFinalData}
              disabled={(!data?.Row || Object.keys(data.Row).length === 0) && 
                      (!oldData?.Row || Object.keys(oldData.Row).length === 0)}
              title={(!data?.Row || Object.keys(data.Row).length === 0) && 
                    (!oldData?.Row || Object.keys(oldData.Row).length === 0) ? 
                    "Please add employees to the current year census table." : ""}
            >
              <img src={saveIcon} alt="Save" className="icon" />
              Save and Continue
            </button>
          </div>
          <ConflictModal
            show={showConflictModal}
            onClose={() => setShowConflictModal(false)}
            conflictValues={conflictValues}
            reportEndDates={reportEndDates}
            onApplyChange={handleConflictChange}
            isRehireConflict={isRehireConflict}
          />
          <BulkConflictModal
            show={showBulkConflictModal}
            onClose={() => setShowBulkConflictModal(false)}
            sources={bulkConflictSources}
            onApplyChange={handleBulkConflictChange}
            bulkConflictField={bulkConflictField}
          />
          <HoursWorkedModal
            show={showHoursModal}
            onClose={() => setShowHoursModal(false)}
            onSave={handleSaveHoursWorked}
            onCalculateGuidantHours={handleCalculateGuidantHours}
            onUpdateTermination={handleUpdateTermination}
            employeeFullName={selectedEmployeeFullName}
            employeeKey={selectedEmployeeKey}
            employeeData={selectedEmployeeData}
            censusYearData={censusYearData}
          />
          <ProcessingModal
            show={showProcessingModal}
            onClose={() => setShowProcessingModal(false)}
            processingStatus={processingStatus}
          />
          <DeleteConfirmationModal
            show={showDeleteModal}
            onClose={handleCloseDeleteModal}
            handleConfirmDeleteRow={handleConfirmDeleteRow}
            handleConfirmDeleteProject={handleConfirmDeleteProject}
            deleteMessage={deleteMessage}
            isDeletingAll={isDeletingAll}
          />
          <ValidationErrorModal
            show={showValidationModal}
            onClose={() => setShowValidationModal(false)}
            errors={validationErrors}
          />
        </div>
      </div>
    </div>
  );
}

export default ResultsContent;